/* Raagas.Shop theme: global behaviours. Vanilla JS, no dependencies. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- dialogs (mobile menu, search, cart drawer) ---------- */
  const lockScroll = (lock) => { document.documentElement.style.overflow = lock ? 'hidden' : ''; };
  window.theme = window.theme || {};
  window.theme.openDialog = (id, opener) => {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) dialog.showModal();
    dialog._opener = opener || document.activeElement;
    lockScroll(true);
    const focus = $('input[type="search"], [data-autofocus]', dialog);
    if (focus) setTimeout(() => focus.focus(), 50);
    return true;
  };
  window.theme.closeDialog = (dialog) => { if (dialog && dialog.open) dialog.close(); };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) { e.preventDefault(); window.theme.openDialog(opener.dataset.dialogOpen, opener); return; }

    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen && window.theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault(); window.theme.openDialog('CartDrawer', cartOpen); return;
    }

    const closer = e.target.closest('[data-dialog-close]');
    if (closer) { window.theme.closeDialog(closer.closest('dialog')); return; }

    // click on the backdrop (the dialog element itself) closes it
    if (e.target instanceof HTMLDialogElement && e.target.hasAttribute('data-dialog')) e.target.close();
  });
  document.addEventListener('close', (e) => {
    if (!(e.target instanceof HTMLDialogElement)) return;
    if (!$$('dialog[open]').length) lockScroll(false);
    const opener = e.target._opener;
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  }, true);

  /* ---------- announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      $('[data-prev]', this)?.addEventListener('click', () => this.go(-1, true));
      $('[data-next]', this)?.addEventListener('click', () => this.go(1, true));
      if (this.dataset.autoplay === 'true' && !prefersReduced) {
        this.speed = Math.max(3000, Number(this.dataset.speed) || 5000);
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
      }
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.go(1), this.speed); }
    stop() { clearInterval(this.timer); }
    go(step) {
      this.slides[this.index].hidden = true;
      this.index = (this.index + step + this.slides.length) % this.slides.length;
      this.slides[this.index].hidden = false;
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- quantity stepper ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-qty-minus]');
        const plus = e.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        e.preventDefault();
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + (plus ? 1 : -1)));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- share button (native share sheet, else copy link) ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = $('[data-share]', this);
      const msg = $('[data-share-msg]', this);
      button?.addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        try {
          if (navigator.share) { await navigator.share(data); return; }
          await navigator.clipboard.writeText(data.url);
          if (msg) { msg.hidden = false; setTimeout(() => { msg.hidden = true; }, 2500); }
        } catch (_) { /* user cancelled */ }
      });
    }
  }
  if (!customElements.get('share-button')) customElements.define('share-button', ShareButton);

  /* ---------- collection filters ---------- */
  const filters = $('[data-filters]');
  if (filters) {
    const setOpen = (open) => {
      filters.classList.toggle('is-open', open);
      $$('[data-filters-open]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
      lockScroll(open && window.matchMedia('(max-width: 1023px)').matches);
    };
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-filters-open]')) setOpen(true);
      else if (e.target.closest('[data-filters-close]')) setOpen(false);
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && filters.classList.contains('is-open')) setOpen(false); });
  }
  // desktop: apply filters as they are toggled; mobile: apply with the "show results" button
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches('[data-auto-submit]') || (el.closest('[data-filters]') && window.matchMedia('(min-width: 1024px)').matches)) {
      el.form?.submit();
    }
  });
  $$('[data-collection-form]').forEach((form) => {
    // drop empty price fields so URLs stay clean
    form.addEventListener('submit', () => {
      $$('input[type="number"]', form).forEach((i) => { if (i.value === '') i.disabled = true; });
    });
  });

  /* ---------- scroll reveal ---------- */
  const reveals = $$('.reveal');
  if (reveals.length) {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const siblings = entry.target.parentElement ? Array.from(entry.target.parentElement.children) : [];
          entry.target.style.transitionDelay = `${Math.min(siblings.indexOf(entry.target), 6) * 70}ms`;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      reveals.forEach((el) => io.observe(el));
    }
  }
})();
